import React from 'react'

import fetchApi, { ContentType, InternalServerError } from '../../utils/fetch_api'
import { HLC, compareHlc, parseHlc, serializeHlc } from '../../utils/hlc'
import { AutoSaveElement, DrawingContainerType } from './constants'

const RETRY_LIMIT = 5

export function useDrawingAutoSaveApi() {
  const retryCountRef = React.useRef(0)

  const fetchDrawings = React.useCallback(
    async ({
      tickers,
      containerTypes,
      lastChange,
    }: {
      tickers: string[]
      containerTypes: DrawingContainerType[]
      lastChange: string
    }) => {
      try {
        const data = await fetchApi<{ drawings: Array<Omit<AutoSaveElement, 'lastChange'> & { lastChange: string }> }>({
          location: '/api/auto_save.ashx',
          queryParameters: {
            ticker: tickers.join(','),
            containerTypes: containerTypes.join(',') || undefined,
            lastChange,
          },
          throwOnStatusCodes: [404],
        })
        return data.drawings.map((drawing) => ({ ...drawing, lastChange: parseHlc(drawing.lastChange) }))
      } catch {
        // ignore error
      }

      return []
    },
    []
  )

  const saveDrawings = React.useCallback(async (drawings: AutoSaveElement[]): Promise<HLC | undefined> => {
    try {
      await fetchApi({
        location: '/api/auto_save.ashx',
        method: 'POST',
        throwOnStatusCodes: [404, 500],

        contentType: ContentType.ApplicationJson,
        body: JSON.stringify(
          drawings.map((item) => ({
            ...item,
            elementAttrs: JSON.stringify(item.elementAttrs),
            lastChange: serializeHlc(item.lastChange),
          }))
        ),
      })
      const latestChangeHlc = [...drawings].sort((a, b) => compareHlc(b.lastChange, a.lastChange))[0]
      retryCountRef.current = 0
      return latestChangeHlc.lastChange
    } catch (error) {
      if (error instanceof InternalServerError) {
        if (retryCountRef.current < RETRY_LIMIT) {
          retryCountRef.current = retryCountRef.current + 1
          await new Promise((r) => setTimeout(r, 1000))
          return saveDrawings(drawings)
        } else {
          retryCountRef.current = 0
        }
      }
      // ignore error
      window.Sentry?.captureMessage('Save drawings error', error)
      return undefined
    }
  }, [])

  return { fetchDrawings, saveDrawings }
}
